import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeSanitize from 'rehype-sanitize';
import style from './markdown-styles.module.css';

const isMarkdown = (text) => {
  if (!text) return false;
  
  // Check for bullet points and numbered lists at the start of lines
  const listPattern = /^(\d+\.\s|[-*+]\s)/m;
  if (listPattern.test(text)) {
    return true;
  }

  // Convert potential numbered list text to proper markdown
  const processNumberedList = (content) => {
    if (!content) return content;
    return content.replace(/^(\d+)[).]\s/gm, '$1. ');
  };

  // Check for other markdown patterns
  const markdownPattern = /(^#{1,6}\s)|(\*\*[^*]+\*\*)|(\*[^*]+\*)|(__[^_]+__)|(_[^_]+_)|(\[[^\]]+\]\([^)]+\))|(`[^`]+`)|(\s*[*-]\s+)|(^\d+\.\s)|(^\|.*\|$)|(^>\s)|(^---$)|(!\[[^\]]*\]\([^)]+\))/m;
  return markdownPattern.test(text);
};

const ContentRenderer = ({ content }) => {
    // Function to process source references without using Redux
    const processSourceReferences = (text) => {
      if (!text) return text;
      
      // Regex to match source references
      const sourceRegex = /(Source:\s+)([^,]+\.pdf)(,\s+Page\s+)(\d+)/gi;
      
      // If no source references, return the original text
      if (!sourceRegex.test(text)) return text;
      
      // Split the text by the regex pattern
      const parts = text.split(sourceRegex);
      if (parts.length === 1) return text;
      
      // Reconstruct the text with the source references
      let result = '';
      for (let i = 0; i < parts.length; i++) {
        if (i % 5 === 0) {
          // This is regular text
          result += parts[i];
        } else if (i % 5 === 1) {
          // This is "Source: "
          result += parts[i];
        } else if (i % 5 === 2) {
          // This is the filename
          result += parts[i];
        } else if (i % 5 === 3) {
          // This is ", Page "
          result += parts[i];
        } else if (i % 5 === 4) {
          // This is the page number
          result += parts[i];
        }
      }
      
      return result;
    };
    
    // Function to convert \n to actual line breaks
    const processLineBreaks = (text) => {
      if (!text) return text;
      // Replace literal \n with actual newlines
      return text.replace(/\\n/g, '\n');
    };
    
    // Check if content is markdown and render accordingly
    const isMarkdownContent = content && (
      content.includes('##') || 
      content.includes('*') || 
      content.includes('```') || 
      content.includes('- ') ||
      content.includes('1. ') ||
      content.includes('|') ||
      content.includes('Source:') // Consider source references as markdown
    );
    
    const processedContent = processSourceReferences(content);
    const contentWithLineBreaks = processLineBreaks(processedContent);
    
    // Process the content to ensure proper numbered list formatting
    const formattedContent = contentWithLineBreaks ? contentWithLineBreaks.replace(/^(\d+)[).]\s/gm, '$1. ') : '';
    
    // Check if content contains HTML table tags or markdown table syntax
    const hasHtmlTable = contentWithLineBreaks && /<table[\s\S]*?<\/table>/i.test(contentWithLineBreaks);
    const hasMarkdownTable = contentWithLineBreaks && /\|[^\|]+\|/.test(contentWithLineBreaks) && contentWithLineBreaks.includes('\n');

    
    if (hasHtmlTable) {
      // If content has HTML tables, render it as HTML (also convert \n to <br>)
      const htmlContent = contentWithLineBreaks.replace(/\n/g, '<br/>');
      return <div className={style.reactMarkDown} dangerouslySetInnerHTML={{ __html: htmlContent }} />;
    } else if (isMarkdownContent || hasMarkdownTable) {
      return (
        <ReactMarkdown
          remarkPlugins={[remarkGfm]}
          rehypePlugins={[rehypeSanitize]}
          className={style.reactMarkDown}
          components={{
            // Override list item rendering to ensure no bold formatting
            li: ({node, ...props}) => <li style={{fontWeight: 'normal', fontSize: '14.4px'}} {...props} />,
            // Ensure paragraphs inside list items aren't bold
            p: ({node, ...props}) => {
              // Check if this paragraph is inside a list item
              const isInListItem = node.parent && node.parent.tagName === 'li';
              return <p style={isInListItem ? {fontWeight: 'normal', fontSize: '14.4px'} : {fontSize: '14.4px'}} {...props} />;
            },
            // Ensure tables are properly styled
            table: ({node, ...props}) => <table className="border-collapse border border-gray-300 my-4 text-[14.4px]" {...props} />,
            thead: ({node, ...props}) => <thead className="bg-gray-100" {...props} />,
            tbody: ({node, ...props}) => <tbody {...props} />,
            tr: ({node, ...props}) => <tr className="border-b border-gray-300" {...props} />,
            th: ({node, ...props}) => <th className="border border-gray-300 px-4 py-2 text-left font-semibold text-[14.4px]" {...props} />,
            td: ({node, ...props}) => <td className="border border-gray-300 px-4 py-2 text-[14.4px]" {...props} />
          }}
        >
          {formattedContent}
        </ReactMarkdown>
      );
    }
    
    // For non-markdown content, convert newlines to <br> tags for proper display
    const simpleContentWithBreaks = contentWithLineBreaks ? contentWithLineBreaks.replace(/\n/g, '<br/>') : '';
    return <div className='simple-answer whitespace-pre-wrap' dangerouslySetInnerHTML={{ __html: simpleContentWithBreaks }} />;
  };

export default ContentRenderer;